import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { Badge, Panel } from '../components/Panel';
import { LocationBars, SalesBars, Trend } from '../charts/Charts';
import { useLoad } from '../lib/useAsync';
import { bogotaDay, formatDate, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

type Preset = 'today' | 'week' | 'month' | 'all' | 'range';

/** Suma días a una fecha de Bogotá (AAAA-MM-DD). */
function shiftDay(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Dashboard gerencial. Todo lo que depende del período elegido (ventas, recaudo, utilidad, rankings, gráfica
 * y rentabilidad por local) se recalcula junto; el inventario y el tránsito son saldos actuales y van aparte.
 */
export function Dashboard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const today = bogotaDay(new Date());

  const [preset, setPreset] = useState<Preset>('today');
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [range, setRange] = useState<{ from?: string; to?: string; all?: boolean }>({ from: today, to: today });

  const { data: d, reload, loading } = useLoad(() => api.dashboard(range), [range.from, range.to, range.all]);
  const profit = useLoad(() => api.profitability(range), [range.from, range.to, range.all]);

  const apply = (p: Preset, f?: string, tt?: string) => {
    setPreset(p);
    if (p === 'all') return setRange({ all: true });
    const desde = f ?? today;
    const hasta = tt ?? today;
    setFrom(desde);
    setTo(hasta);
    setRange({ from: desde, to: hasta });
  };
  const clear = () => apply('today');
  const applyRange = (e: FormEvent) => { e.preventDefault(); apply('range', from, to); };

  const money = (n: number) => formatMoney(n, lang);
  const num = (n: number) => formatNumber(n, lang);
  const day = (value: string) => formatDate(`${value.slice(0, 10)}T12:00:00-05:00`, lang, false);

  const periodBar = (
    <form className="actions" id="dashPeriod" style={{ marginBottom: 12, alignItems: 'end', flexWrap: 'wrap' }} onSubmit={applyRange}>
      <button type="button" className={`btn sm${preset === 'today' ? ' black' : ''}`} data-range="today" onClick={() => apply('today')}>{t('dashboard.period.today')}</button>
      <button type="button" className={`btn sm${preset === 'week' ? ' black' : ''}`} data-range="week" onClick={() => apply('week', shiftDay(today, -6))}>{t('dashboard.period.week')}</button>
      <button type="button" className={`btn sm${preset === 'month' ? ' black' : ''}`} data-range="month" onClick={() => apply('month', shiftDay(today, -29))}>{t('dashboard.period.month')}</button>
      <button type="button" className={`btn sm${preset === 'all' ? ' black' : ''}`} data-range="all" onClick={() => apply('all')}>{t('dashboard.period.all')}</button>
      <div className="field">
        <label htmlFor="dashFrom">{t('dashboard.period.from')}</label>
        <input className="input" id="dashFrom" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="dashTo">{t('dashboard.period.to')}</label>
        <input className="input" id="dashTo" type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
      </div>
      <button type="submit" className="btn" id="dashApply">{t('dashboard.period.apply')}</button>
      <button type="button" className="btn sm" id="dashClear" onClick={clear}>{t('dashboard.period.clear')}</button>
      <span className="help" id="dashRange">
        {d ? (d.from === d.to ? t('dashboard.period.showingDay', { day: day(d.from) }) : t('dashboard.period.showing', { from: day(d.from), to: day(d.to), days: num(d.days) })) : ''}
      </span>
    </form>
  );

  if (!d) {
    return (
      <>
        {periodBar}
        {loading ? (
          <div className="kpis" aria-busy="true" aria-label={t('dashboard.loading')}>
            {Array.from({ length: 6 }, (_, i) => (
              <div className="kpi" key={i}>
                <span className="skeleton" style={{ width: '55%' }} />
                <span className="skeleton big" style={{ width: '75%' }} />
                <span className="skeleton" style={{ width: '40%' }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="empty">{t('common.noData')}</div>
        )}
      </>
    );
  }

  const s = d.period.sales;
  const units = d.current.inventory.reduce((a, l) => a + l.units, 0);
  const value = d.current.inventory.reduce((a, l) => a + l.value, 0);
  const returnsNote = (amount: number) => (amount > 0 ? ` · ${t('dashboard.returnsSub', { amount: money(amount) })}` : '');
  const bucketLabel = t(`dashboard.bucket.${d.bucket}`);

  return (
    <>
      {periodBar}

      <div className="kpis" id="dashKpis">
        <div className="kpi">
          <div className="lab">{t('dashboard.salesPeriod')}</div>
          <div className="val" id="dSales">{money(s.total)}</div>
          <Trend id="dTrend" current={s.total} previous={d.previous.total} label={t('dashboard.vsPrevious')} />
          <div className="sub" id="dSalesSub">{t('dashboard.salesSub', { n: num(s.count), units: num(s.units) })}{returnsNote(s.returns)}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('dashboard.collected')}</div>
          <div className="val" id="dCollected">{money(s.collected)}</div>
          <div className="sub">{t('dashboard.collectedSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('dashboard.profit')}</div>
          <div className="val" id="dProfit">{money(s.margin)}</div>
          <div className="sub" id="dMargin">{t('dashboard.profitSub', { pct: num(s.margin_pct) })}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('dashboard.unitsSold')}</div>
          <div className="val" id="dUnitsSold">{num(s.units)}</div>
          <div className="sub">{t('dashboard.unitsSoldSub', { store: money(s.store), direct: money(s.direct) })}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('dashboard.units')}</div>
          <div className="val" id="dUnits">{num(units + d.current.transit.units)}</div>
          <div className="sub">{t('dashboard.unitsSub', { transit: num(d.current.transit.units) })}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('dashboard.value')}</div>
          <div className="val" id="dValue">{money(value + d.current.transit.value)}</div>
          <div className="sub">{t('dashboard.valueSub')}</div>
        </div>
      </div>

      <div className="g2" style={{ marginBottom: 14 }}>
        <Panel title={t('dashboard.salesChartTitle')} description={t('dashboard.salesChartHelp', { bucket: bucketLabel })}>
          <SalesBars
            id="dashSalesChart" points={d.period.daily} formatValue={money} formatDay={day}
            tableCaption={t('dashboard.salesChartTitle')} emptyLabel={t('dashboard.salesChartEmpty')} dayLabel={bucketLabel} valueLabel={t('dashboard.salesColumn')}
          />
        </Panel>
        <Panel title={t('dashboard.methodsTitle')} description={t('dashboard.methodsHelp')}>
          {d.period.by_method.length === 0 ? (
            <div className="empty">{t('dashboard.noSales')}</div>
          ) : (
            <div className="tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('dashboard.methodCols.method')}</th>
                    <th className="num">{t('dashboard.methodCols.amount')}</th>
                    <th>{t('dashboard.methodCols.counts')}</th>
                  </tr>
                </thead>
                <tbody id="dashMethods">
                  {d.period.by_method.map((m) => (
                    <tr key={m.payment_method_id} data-method={m.code}>
                      <td>{m.name}</td>
                      <td className="num" data-col="amount">{money(m.amount)}</td>
                      <td>{m.collected ? <Badge tone="green">{t('dashboard.counts')}</Badge> : <Badge tone="amber">{t('dashboard.notCounts')}</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <div className="g2">
        <Panel
          title={t('dashboard.alertsTitle')}
          description={t('dashboard.alertsHelp')}
          badge={
            <button type="button" className="btn sm" id="dashRefresh" onClick={() => void reload()}>
              {t('dashboard.refresh')}
            </button>
          }
        >
          <div className="alerts" id="dashAlerts">
            <div className="alert" data-alert="pending">
              <div>
                <b>{t('dashboard.pending', { n: num(d.current.pending_shipments.count) })}</b>
                <small>{d.current.pending_shipments.count > 0 ? t('dashboard.pendingOldest', { hours: num(d.current.pending_shipments.oldest_hours) }) : t('dashboard.pendingNone')}</small>
              </div>
              <Badge tone={d.current.pending_shipments.count > 0 ? 'amber' : 'green'}>{num(d.current.pending_shipments.count)}</Badge>
            </div>
            <div className="alert" data-alert="differences">
              <div>
                <b>{t('dashboard.differences', { n: num(d.current.open_differences.count) })}</b>
                <small>{t('dashboard.differencesSub', { missing: num(d.current.open_differences.missing), damaged: num(d.current.open_differences.damaged), surplus: num(d.current.open_differences.surplus) })}</small>
              </div>
              <Badge tone={d.current.open_differences.count > 0 ? 'red' : 'green'}>{num(d.current.open_differences.count)}</Badge>
            </div>
            <div className="alert" data-alert="lowStock">
              <div>
                <b>{t('dashboard.lowStock', { n: num(d.current.low_stock.count) })}</b>
                <small>{d.current.low_stock.items.slice(0, 5).map((i) => `${i.location_code} ${i.ref} (${i.qty}/${i.min_stock})`).join(' · ') || t('dashboard.lowStockNone')}</small>
              </div>
              <Badge tone={d.current.low_stock.count > 0 ? 'amber' : 'green'}>{num(d.current.low_stock.count)}</Badge>
            </div>
            <div className="alert" data-alert="belowCost">
              <div>
                <b>{t('dashboard.belowCost', { n: num(d.current.below_cost) })}</b>
                <small>{t('dashboard.belowCostSub')}</small>
              </div>
              <Badge tone={d.current.below_cost > 0 ? 'red' : 'green'}>{num(d.current.below_cost)}</Badge>
            </div>
            <div className="alert" data-alert="kardex">
              <div>
                <b>{t(d.current.kardex_differences === 0 ? 'kardex.checkOk' : 'kardex.checkBad', { n: d.current.kardex_differences })}</b>
                <small>{t('dashboard.kardexSub')}</small>
              </div>
              <Badge tone={d.current.kardex_differences === 0 ? 'green' : 'red'}>{num(d.current.kardex_differences)}</Badge>
            </div>
          </div>
        </Panel>

        <Panel title={t('dashboard.bestTitle')} description={t('dashboard.bestHelp')}>
          <div className="alerts" id="dashBest">
            <div className="alert">
              <div>
                <b>{t('dashboard.topLocation')}</b>
                <small>{d.period.top_location ? `${d.period.top_location.name} · ${money(d.period.top_location.total)}` : t('dashboard.noSales')}</small>
              </div>
            </div>
            <div className="alert">
              <div>
                <b>{t('dashboard.topProduct')}</b>
                <small>{d.period.top_product ? `${d.period.top_product.ref} ${d.period.top_product.name} · ${t('ops.unitsCount', { n: num(d.period.top_product.units) })}` : t('dashboard.noSales')}</small>
              </div>
            </div>
            <div className="alert" data-alert="topMargin">
              <div>
                <b>{t('dashboard.topMargin')}</b>
                <small id="dashTopMargin">
                  {d.period.top_margin.length > 0
                    ? d.period.top_margin.map((p) => `${p.ref} ${money(p.margin)}`).join(' · ')
                    : t('dashboard.noSales')}
                </small>
              </div>
            </div>
          </div>
        </Panel>
      </div>

      {profit.data ? (
        <div style={{ marginTop: 14 }}>
          <Panel
            title={t('dashboard.profitTitle')}
            description={t('dashboard.profitHelp', { days: profit.data.days })}
          >
            <div className="tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('dashboard.cols.location')}</th>
                    <th className="num">{t('dashboard.profitCols.sales')}</th>
                    <th className="num">{t('dashboard.profitCols.margin')}</th>
                    <th className="num">{t('dashboard.profitCols.fixed')}</th>
                    <th className="num">{t('dashboard.profitCols.result')}</th>
                  </tr>
                </thead>
                <tbody id="dashProfit">
                  {profit.data.locations.filter((l) => l.active).map((l) => (
                    <tr key={l.location_id} data-location={l.code}>
                      <td>
                        <b>{l.name}</b>
                        <br />
                        <span className="tiny muted">{l.code}</span>
                      </td>
                      <td className="num" data-col="sales">{money(l.sales)}</td>
                      <td className="num" data-col="margin">{t('locations.stats.marginValue', { amount: money(l.gross_margin), pct: l.sales > 0 ? Math.round((l.gross_margin / l.sales) * 100) : 0 })}</td>
                      <td className="num" data-col="fixed" title={t('dashboard.profitFixedTitle', { month: money(l.fixed_month) })}>{money(l.fixed_prorated)}</td>
                      <td className={`num ${l.result < 0 ? 'auditneg' : 'auditpos'}`} data-col="result">{money(l.result)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="help" id="dashProfitNote" style={{ marginTop: 10 }}>{t('dashboard.profitNote')}</p>
          </Panel>
        </div>
      ) : null}

      <div className="g2" style={{ marginTop: 14 }}>
        <Panel title={t('dashboard.inventoryTitle')} description={t('dashboard.inventoryHelp')} badge={<Badge tone="blue">{t('dashboard.currentBalances')}</Badge>}>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>{t('dashboard.cols.location')}</th>
                  <th className="num">{t('dashboard.cols.units')}</th>
                  <th className="num">{t('dashboard.cols.value')}</th>
                  <th className="num">{t('dashboard.cols.minimum')}</th>
                  <th>{t('common.status')}</th>
                </tr>
              </thead>
              <tbody id="dashInventory">
                {d.current.inventory.map((l) => (
                  <tr key={l.location_id} data-location={l.code}>
                    <td>
                      <b>{l.name}</b>
                      <br />
                      <span className="tiny muted">{l.code}</span>
                    </td>
                    <td className="num" data-col="units">{num(l.units)}</td>
                    <td className="num" data-col="value">{money(l.value_internal || l.value)}</td>
                    <td className="num">{num(l.min_total)}</td>
                    <td>{l.units < l.min_total ? <Badge tone="amber">{t('inventory.low')}</Badge> : <Badge tone="green">{t('inventory.ok')}</Badge>}</td>
                  </tr>
                ))}
                <tr>
                  <td>
                    <b>{t('dashboard.inTransit')}</b>
                  </td>
                  <td className="num" data-col="units">{num(d.current.transit.units)}</td>
                  <td className="num">{money(d.current.transit.value)}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="help" style={{ marginTop: 8 }}>{t('dashboard.inventoryCostNote')}</p>
        </Panel>
        <Panel title={t('dashboard.stockChartTitle')} description={t('dashboard.stockChartHelp')} badge={<Badge tone="blue">{t('dashboard.currentBalances')}</Badge>}>
          <LocationBars id="dashStockChart" rows={d.current.inventory} formatValue={(n) => t('ops.unitsCount', { n: num(n) })} />
        </Panel>
      </div>
    </>
  );
}
